import { Component, Inject, OnDestroy, OnInit, Optional } from "@angular/core";
import { VNG_DIALOG_DATA, VngDialogRef, VngSelectOption } from "@vengreso/angular-components";
import { map } from "rxjs/operators";
import { DataService } from "../../core-services/services/dataservices/data.service";
import { Observable, Subscription } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { WarningDialogComponent } from "src/app/core/core-services/warning-dialog/warning-dialog.component";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { getPlanId } from "src/app/core/core-services/constants/plans.constants";
import { FormBuilder, FormControl, FormGroup } from "@angular/forms";
import { SharedDataService } from "../../core-services/services/shared-data.service";

@Component({
    // tslint:disable-next-line:component-selector
    selector: "app-advanced-search-dialog",
    templateUrl: "./advanced-search-dialog.component.html",
    styleUrls: ["./advanced-search-dialog.component.css"],
    standalone: false
})
export class AdvancedSearchDialogComponent implements OnInit, OnDestroy {
  flycut: string;
  title: string;
  hasTheWords: string;
  doesntHave: string;
  createdWithin: string = "any-time";
  bySearch: string = "all-flymsg";
  hasAttachment: boolean;
  hasHyperlink: boolean;
  date: any;
  filteredOptions: Observable<any>;
  form: FormGroup;
  isDialogClosed = true;
  formSubscription: Subscription

  // R5 — createdWithin is a plain, non-grouped vng-select.
  readonly createdWithinOptions: VngSelectOption[] = [
    { value: "1d", label: "1 day" },
    { value: "3d", label: "3 days" },
    { value: "1w", label: "1 week" },
    { value: "2w", label: "2 weeks" },
    { value: "1m", label: "1 month" },
    { value: "any-time", label: "Any time" },
  ];

  // 04 §3.4 / 03 R5 optgroup composition — the 3 static options come first,
  // then each FlyCuts/FlyPlates category group is flattened into a disabled
  // header option (unselectable + keyboard-skipped for free, per
  // vng-select.component.ts:329,450-452) followed by its items.
  private readonly staticBySearchOptions: VngSelectOption[] = [
    { value: "all-flymsg", label: "All FlyMSG" },
    { value: "flycuts", label: "FlyCuts" },
    { value: "flyplates", label: "FlyPlates" },
  ];
  bySearchOptions: VngSelectOption[] = [...this.staticBySearchOptions];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dataService: DataService,
    public dialogRef: VngDialogRef<AdvancedSearchDialogComponent>,
    public dialog: DialogService,
    private readonly sharedData: SharedDataService,
    private readonly fb: FormBuilder,
    @Optional() @Inject(VNG_DIALOG_DATA) public mydata: any
  ) {
    this.form = this.fb.group({
      flycut: new FormControl(null),
      title: new FormControl(null),
      hasTheWords: new FormControl(null),
      doesntHave: new FormControl(null),
      createdWithin: new FormControl("any-time"),
      date: new FormControl(null),
      bySearch: new FormControl("all-flymsg"),
      hasAttachment: new FormControl(false),
      hasHyperlink: new FormControl(false),
    });

    this.formSubscription = this.form.valueChanges.subscribe((event) => {
      if (event.hasAttachment) {
        if (!this.canEdit()) {
          this.form.get("hasAttachment").patchValue(false);
          return;
        }
      }
      if (event.hasHyperlink) {
        if (!this.canEdit()) {
          this.form.get("hasHyperlink").patchValue(false);
          return;
        }
      }
      if (event.title) {
        if (!this.canEdit()) {
          this.form.get("title").patchValue("");
          return;
        }
      }
      if (event.flycut) {
        if (!this.canEdit()) {
          this.form.get("flycut").patchValue("");
          return;
        }
      }
      if (event.hasTheWords) {
        if (!this.canEdit()) {
          this.form.get("hasTheWords").patchValue("");
          return;
        }
      }
      if (event.doesntHave) {
        if (!this.canEdit()) {
          this.form.get("doesntHave").patchValue("");
          return;
        }
      }
      if (event.date) {
        if (!this.canEdit()) {
          this.form.get("date").patchValue("");
          return;
        }
      }
      if (event.createdWithin && event.createdWithin != "any-time") {
        if (!this.canEdit()) {
          this.form.get("createdWithin").patchValue("any-time");
          return;
        }
      }
      if (event.bySearch && event.bySearch != "all-flymsg") {
        if (!this.canEdit()) {
          this.form.get("bySearch").patchValue("all-flymsg");
          return;
        }
      }
    });
  }

  async ngOnInit(): Promise<void> {
    this.filteredOptions = await this.categories();
    this.filteredOptions.subscribe((groups: any[]) => {
      this.bySearchOptions = [
        ...this.staticBySearchOptions,
        ...(groups || []).flatMap((group: any) => [
          { value: `__hdr_${group.type}`, label: group.type, disabled: true, data: { header: true } } as VngSelectOption,
          ...(group.items || []).map(
            (item: any) => ({ value: item.id, label: item.name, data: { grouped: true } } as VngSelectOption)
          ),
        ]),
      ];
    });
  }

  ngOnDestroy(): void {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  resetSearch() {
    this.form.patchValue({
      flycut: null,
      title: null,
      hasTheWords: null,
      doesntHave: null,
      createdWithin: "any-time",
      date: null,
      bySearch: "all-flymsg",
      hasAttachment: false,
      hasHyperlink: false,
    });
    this.sharedData.setAttribute("advancedSearchData", null);
  }

  closeDialog() {
    this.resetSearch()
    this.dialogRef.close({ event: "close", data: "" });
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  advancedSearch(type: string) {
    let planId = getPlanId();
    if (
      planId != "Free" &&
      !this.form.value.date &&
      type == "createdWithin" &&
      this.form.value.createdWithin != "any-time"
    )
      this.form.get("date").setValue(new Date());

    // Build payload with only truthy/non-empty values; empty fields are not sent
    const formValue = this.form.value;
    const params: Record<string, any> = {};

    if (formValue.flycut) params.flycut = formValue.flycut;
    if (formValue.title) params.title = formValue.title;
    if (formValue.hasTheWords) params.hasTheWords = formValue.hasTheWords;
    if (formValue.doesntHave) params.doesntHave = formValue.doesntHave;

    const dateStr = this.getDate();
    if (dateStr) params.date = dateStr;

    if (formValue.createdWithin && formValue.createdWithin !== "any-time") {
      params.createdWithin = formValue.createdWithin;
    }
    if (formValue.bySearch && formValue.bySearch !== "all-flymsg") {
      params.bySearch = formValue.bySearch;
    }
    if (formValue.hasAttachment) params.hasAttachment = formValue.hasAttachment;
    if (formValue.hasHyperlink) params.hasHyperlink = formValue.hasHyperlink;

    this.sharedData.setAttribute("advancedSearchData", JSON.stringify(params));

    if (type == "search") {
      if (!this.canEdit()) {
        return;
      }
      this.router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
        this.router.navigate(["/advanced-search-result"]);
      });
    }
  }

  categories(): any {
    return this.dataService
      .getCategories()
      .pipe(map((tracks) => tracks.result.data));
  }

  getDate(): string | null {
    const createdWithin = this.form.value.createdWithin;

    if (!createdWithin || createdWithin === "any-time") {
      return null;
    }

    const daysMap: Record<string, number> = {
      "1d": 1, "3d": 3, "1w": 7, "2w": 14, "1m": 31,
    };

    let d: Date;
    if (daysMap[createdWithin] !== undefined) {
      d = new Date();
      d.setDate(d.getDate() - daysMap[createdWithin]);
    } else {
      // custom: use the datepicker value directly. vng-datepicker (R16) writes
      // MM/DD/YYYY strings back to the control on user change (or a Date when
      // set programmatically, e.g. advancedSearch()'s `new Date()` above) —
      // the native Date constructor parses both forms.
      const raw = this.form.value.date;
      if (!raw) return null;
      d = new Date(raw);
    }

    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  keyDownFunction(event) {
    if (event.keyCode === 13) {
      this.closeDialog();
      this.advancedSearch("search");
    }
  }

  handleCloseDialog() {
    this.isDialogClosed = true;
  }

  canEdit() {
    let planId = getPlanId();
    if (planId == "Free") {
      if (this.isDialogClosed) {
        this.isDialogClosed = false;
        this.dialog.openDialogComponent(
          WarningDialogComponent,
          {
            self: this,
            type: "advancedSearch",
            showTheStarter: planId === "Free",
            error: "advancedSearch",
          },
          "800px"
        );
      }
      return false;
    }
    return true;
  }
}
